import {
  Action as ActionSchema,
  IDEMPOTENCY_KEY_HEADER,
  actionCategory,
  actionPrimary,
  type Action,
  type ActionClient,
  type CreateActionBody,
  type CreateFeedbackBody,
  type R1Detail,
  type R1PrepFields,
  type R1PrepInput,
  type R1PrepResult,
  type UpdateActionBody,
  type UpdateChecklistItemBody,
} from '@cockpit/shared';
import { ApiError, api } from '../api';
import { request } from '../api/client';
import { useOfflineStore } from '../stores/offline';
import {
  applyChecklist,
  applyUpdate,
  baseFields,
  cleanText,
  clientStub,
  overlayR1,
  type ActionUpdateOp,
  type ChecklistAddOp,
  type ChecklistDeleteOp,
  type ChecklistUpdateOp,
  type R1PrepOp,
} from './ops';

/**
 * Lot AW : gestes internes qui marchent aussi sans réseau. Avec le réseau (et rien en attente sur la même carte),
 * ils partent comme avant ; sans réseau, ou si l'envoi échoue faute de réseau, ils sont gardés dans la file et
 * l'écran reçoit tout de suite la carte telle qu'elle sera (affichage optimiste, pastille « en attente d'envoi »).
 */

function isNetwork(err: unknown): boolean {
  return err instanceof ApiError && err.status === 0;
}

/**
 * Envoi direct si possible ; gardé dans la file sinon (pas de réseau, ou changements plus anciens sur la cible).
 * La clé d'idempotence de l'envoi direct est reprise par le changement gardé : si la réponse s'est perdue, le
 * renvoi ne refait rien.
 */
async function directOrQueue<T>(
  target: string,
  direct: (key: string) => Promise<T>,
  queue: (key: string) => Promise<T>,
): Promise<T> {
  const offline = useOfflineStore();
  const key = crypto.randomUUID();
  if (offline.mustQueue(target)) return queue(key);
  try {
    return await direct(key);
  } catch (err) {
    if (!isNetwork(err)) throw err;
    offline.goneOffline();
    return queue(key);
  }
}

const idem = (key: string) => ({ [IDEMPOTENCY_KEY_HEADER]: key });

const now = () => new Date().toISOString();

/** Modifier une carte (date, personne, titre, description, client, projet) ou l'écarter / la rouvrir. */
export function updateAction(
  action: Action,
  body: UpdateActionBody,
  extra: { client?: ActionClient | null } = {},
): Promise<Action> {
  const offline = useOfflineStore();
  return directOrQueue(
    `action:${action.id}`,
    () => api.updateAction(action.id, body),
    async (key) => {
      const patch: Omit<ActionUpdateOp, 'id' | 'seq' | 'userId' | 'createdAt' | 'state'> = {
        kind: 'action.update',
        actionId: action.id,
        title: action.title,
        body,
        base: { updatedAt: action.updatedAt, fields: baseFields(action, body) },
        ...(extra.client !== undefined ? { client: extra.client } : {}),
        ...(body.status === 'OPEN' ? { snapshot: action } : {}),
      };
      await offline.add(patch as never, key);
      return applyUpdate(action, patch);
    },
  );
}

/** Nom d'un client pour l'affichage optimiste. */
export function clientFor(id: string | null | undefined, options: readonly { id: string; name: string }[]) {
  if (!id) return null;
  const found = options.find((c) => c.id === id);
  return clientStub(id, found?.name ?? '');
}

/** Tâche ajoutée à la main ; sans réseau, elle apparaît tout de suite avec un identifiant provisoire. */
export function createAction(
  body: CreateActionBody,
  context: { authorId: string; authorName: string; client: ActionClient | null; projectName: string | null },
): Promise<Action> {
  const offline = useOfflineStore();
  const queue = async (key: string): Promise<Action> => {
    const id = crypto.randomUUID();
    const contextName = context.projectName ?? context.client?.name ?? null;
    const snapshot: Action = {
      id,
      type: 'user_task',
      category: actionCategory('user_task'),
      origin: 'USER',
      ruleKey: null,
      ruleLabel: 'tâche ajoutée à la main',
      title: body.title.trim(),
      why: `${contextName ? `${context.projectName ? 'Projet ' : ''}${contextName} · ` : ''}ajoutée par ${context.authorName}`,
      amountCents: null,
      amountNote: null,
      dueDate: body.dueDate,
      assigneeId: body.assigneeId,
      status: 'OPEN',
      replySnippet: null,
      client: context.client,
      projectId: body.projectId ?? null,
      stepId: null,
      primary: actionPrimary('user_task', 'OPEN', null, {}),
      payload: { createdBy: context.authorId },
      createdAt: now(),
      updatedAt: null,
      doneAt: null,
      doneBy: null,
      description: cleanText(body.description),
      checklist: (body.steps ?? [])
        .map((label) => label.trim())
        .filter((label) => label !== '')
        .map((label) => ({ id: crypto.randomUUID(), label, done: false, doneAt: null, doneBy: null })),
      attachments: [],
    };
    await offline.add(
      { kind: 'action.create', actionId: id, title: snapshot.title, body, snapshot } as never,
      key,
    );
    return snapshot;
  };
  // Une nouvelle tâche n'a pas de cible déjà en file : envoi direct avec le réseau.
  return directOrQueue(
    'action:new',
    (key) => request('POST', '/api/actions', { body, schema: ActionSchema, headers: idem(key) }),
    queue,
  );
}

/** « Fait » sur une tâche ajoutée à la main, sans réseau : gardé (plan `action.done` au retour du réseau). */
/** Rend le changement gardé (lot BI : « Annuler » le retire de la file tant qu'il n'est pas parti). */
export async function queueDone(action: Action): Promise<{ id: string }> {
  return useOfflineStore().add({ kind: 'action.done', actionId: action.id, title: action.title } as never);
}

export function addChecklistItem(action: Action, label: string): Promise<Action> {
  return directOrQueue(
    `action:${action.id}`,
    (key) =>
      request('POST', `/api/actions/${encodeURIComponent(action.id)}/checklist`, {
        body: { label },
        schema: ActionSchema,
        headers: idem(key),
      }),
    async (key) => {
      const op = {
        kind: 'checklist.add',
        actionId: action.id,
        itemId: crypto.randomUUID(),
        label,
        title: action.title,
      };
      const saved = (await useOfflineStore().add(op as never, key)) as ChecklistAddOp;
      return applyChecklist(action, saved, now());
    },
  );
}

export function updateChecklistItem(
  action: Action,
  itemId: string,
  body: UpdateChecklistItemBody,
): Promise<Action> {
  return directOrQueue(
    `action:${action.id}`,
    () => api.updateChecklistItem(action.id, itemId, body),
    async (key) => {
      const op = { kind: 'checklist.update', actionId: action.id, itemId, body, title: action.title };
      const offline = useOfflineStore();
      await offline.add(op as never, key);
      return applyChecklist(action, { ...op, userId: offline.owner ?? '' } as ChecklistUpdateOp, now());
    },
  );
}

export function deleteChecklistItem(action: Action, itemId: string): Promise<Action> {
  return directOrQueue(
    `action:${action.id}`,
    () => api.deleteChecklistItem(action.id, itemId),
    async (key) => {
      const op = { kind: 'checklist.delete', actionId: action.id, itemId, title: action.title };
      await useOfflineStore().add(op as never, key);
      return applyChecklist(action, op as ChecklistDeleteOp, now());
    },
  );
}

/** Infos d'un R1 (champs ou « Rien à ajouter ») ; `detail` = la version vue (pour les conflits). */
export function putR1Prep(
  r1Key: string,
  input: R1PrepInput,
  seen: { detail: R1Detail | null; title: string },
): Promise<R1PrepResult> {
  return directOrQueue(
    `r1:${r1Key}`,
    () => api.putR1Prep(r1Key, input),
    async (key) => {
      const fields: Partial<R1PrepFields> = {};
      if ('fields' in input) {
        for (const key of Object.keys(input.fields) as (keyof R1PrepFields)[]) {
          fields[key] = seen.detail?.prep?.fields[key] ?? null;
        }
      }
      const op: Omit<R1PrepOp, 'id' | 'seq' | 'userId' | 'createdAt' | 'state'> = {
        kind: 'r1.prep',
        r1Key,
        input,
        title: seen.title,
        base: { filledAt: seen.detail?.prep?.filledAt ?? null, fields },
      };
      const saved = (await useOfflineStore().add(op as never, key)) as R1PrepOp;
      const detail = seen.detail ? overlayR1(seen.detail, [saved]) : null;
      return { detail: detail as R1Detail, consequences: [] };
    },
  );
}

/** Retour avec photos fait sans réseau : gardé avec ses photos, envoyé au retour du réseau. */
export async function queueFeedback(body: CreateFeedbackBody, files: readonly Blob[]): Promise<void> {
  const offline = useOfflineStore();
  const blobIds: string[] = [];
  for (const file of files) blobIds.push(await offline.putBlob(file));
  await offline.add({
    kind: 'feedback.create',
    title: body.text,
    body,
    blobIds,
    uploaded: 0,
  } as never);
}
